import { Text } from '@backend/lib-design-system';
import { type ComponentProps, forwardRef } from 'react';
import { styled } from 'styled-components';

import { Column } from '~/components/Column';
import { Container } from '~/components/Container/Container';
import { Link } from '~/components/Link';
import { Row } from '~/components/Row';
import { MobileOnly, NotMobile } from '~/components/Styled';
import { useIsPromanBusinessUnit } from '~/hooks';
import { useBrandName } from '~/hooks/useBrandName';
import { useHref } from '~/hooks/useHref';
import { getMediaQuery } from '~/utils/responsive/media';

import { FooterLegalMenu } from './FooterLegalMenu/footerLegalMenu';
import { FooterMenu } from './FooterMenu/footerMenu';
import { FooterSocialMenu } from './FooterSocialMenu/footerSocialMenu';

const StyledFooter = styled(Container)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.secondary.normal};

  * {
    color: white;
  }
  a {
    text-decoration: none;
  }
`;

const StyledFooterContainer = styled(Column)`
  ${getMediaQuery('mobile')} {
    gap: 64px;
    align-items: center;
  }
`;

const StyledFooterMenuWrapper = styled(Column)`
  flex: 1;
`;

const StyledFooterLogo = styled.img<{ $isProman?: boolean }>`
  width: ${({ $isProman }) => ($isProman ? '120px' : '48px')};
  height: ${({ $isProman }) => ($isProman ? '25px' : '48px')};
  ${({ $isProman }) => $isProman && 'margin: 0 auto; display: block;'}
`;

const StyledCopyright = styled(Text)`
  color: #93a3c0;
`;

export const Footer = forwardRef<HTMLDivElement, ComponentProps<'footer'>>((_props, ref) => {
  const { baseHref } = useHref();
  const brandName = useBrandName();
  const isProman = useIsPromanBusinessUnit();

  return (
    <StyledFooter ref={ref}>
      <StyledFooterContainer>
        {isProman ? (
          <Link href={baseHref}>
            <StyledFooterLogo src="/images/proman-logo.svg" alt={brandName} $isProman />
          </Link>
        ) : (
          <Row>
            <NotMobile>
              <Link href={baseHref}>
                <StyledFooterLogo src="/images/icon-app-v2.svg" alt="Iziwork Logo" />
              </Link>
            </NotMobile>
            <StyledFooterMenuWrapper>
              <FooterMenu />
              <FooterSocialMenu />
            </StyledFooterMenuWrapper>
          </Row>
        )}
        <FooterLegalMenu />
        {!isProman && (
          <>
            <MobileOnly>
              <Link href={baseHref}>
                <StyledFooterLogo src="/images/icon-app-v2.svg" alt="Iziwork Logo" />
              </Link>
            </MobileOnly>
            <StyledCopyright variant="caption">
              Copyright © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {brandName}
            </StyledCopyright>
          </>
        )}
      </StyledFooterContainer>
    </StyledFooter>
  );
});
